"use client";
import { useTranslations } from "@zapier/i18n";

import { useMemo, useState } from "react";

import {
  Avatar,
  Button,
  Key,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import { UNTITLED_ZAP_DEFAULT, useNotification } from "@zapier/utils";
import {
  getMessageFromNetworkError,
  useChangeOwner,
  useQuerySearchMembers,
  Zap,
} from "@zapier/client";
import styles from "./ZapChangeOwnerModal.module.css";
import { FilterTypeahead } from "../../common/FilterTypeahead";

interface Props {
  zaps: Zap[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  currentOwnerId?: number | string;
  onSettled?: () => void;
  folderId?: string; // used for cache invalidation
}

interface ContentProps extends Props {
  onSubmit: (memberKey: Key, memberName: string) => void;
}

function ZapChangeOwnerModalContent({
  zaps,
  currentOwnerId = "",
  setIsOpen,
  onSubmit,
}: ContentProps) {
  const t = useTranslations("zap-change-owner-modal");

  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<Key>(
    currentOwnerId ? Number(currentOwnerId) : "",
  );

  const { members } = useQuerySearchMembers({
    search: searchValue,
    allowTeams: true,
  });

  const items = useMemo(() => {
    const mapped = members.map((member) => ({
      ...member,
      id: member.id,
      name: member.fullName,
    }));
    return mapped;
  }, [members]);

  // callback fired when a key is selected from the dropdown
  const selectKey = (key: Key | null) => {
    setSelectedKey(key || "");
  };

  const searchChange = (search: string) => {
    setSearchValue(search);
  };

  const submit = () => {
    const memberName =
      members.find((member) => member.id == selectedKey)?.fullName || "";
    onSubmit(selectedKey, memberName);
  };

  const isValid = !!selectedKey && selectedKey !== currentOwnerId;

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            event.stopPropagation();
            if (!isValid) return;
            return event.key === "Enter" && submit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.change-owner-title", {
            count: zaps.length,
            title: zaps[0]?.title || UNTITLED_ZAP_DEFAULT,
          })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <div className={styles.body}>
            <Text type="Body1" tag="p">
              {t("body.ownership-transfer-warning", { count: zaps.length })}
            </Text>
            <div className={styles.divider} />
            <FilterTypeahead
              // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the new-owner field when the modal opens
              autoFocus
              isRequired
              items={items}
              label={t("form.new-owner-label")}
              onSearchChange={searchChange}
              onSelectKey={selectKey}
              placeholder={t("form.select-placeholder")}
              selectedKey={selectedKey}
              renderItem={(item: any) => {
                return (
                  <div className={styles.menuItem}>
                    <Avatar url={item.photoUrl} name={item.fullName} />
                    <Text tag="p">{item.fullName}</Text>
                  </div>
                );
              }}
            />
          </div>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" isDisabled={!isValid} onPress={submit}>
            {t("buttons.change-owner")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export function ZapChangeOwnerModal(props: Props) {
  const t = useTranslations("zap-change-owner-modal");

  const { zaps, isOpen, setIsOpen, onSettled, folderId } = props;
  const { createNotification } = useNotification();
  const { changeOwner } = useChangeOwner({ folderId });

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const submit = (ownerId: Key, ownerName: string) => {
    changeOwner(
      {
        zapIds: zaps.map((zap) => zap.id),
        ownerId: String(ownerId),
      },
      {
        onSuccess: (response) => {
          const success = response.filter((r) => r.status === "fulfilled");
          const failure = response.filter((r) => r.status === "rejected");

          if (success.length > 1) {
            createNotification({
              message: (
                <Text>
                  {t("notifications.bulk-success", {
                    count: success.length,
                    ownerName: ownerName,
                  })}
                </Text>
              ),
              type: "success",
            });
          } else if (success.length === 1) {
            createNotification({
              message: (
                <Text>
                  {t("notifications.single-success", {
                    title: zaps[0]?.title || UNTITLED_ZAP_DEFAULT,
                    ownerName: ownerName,
                  })}
                </Text>
              ),
              type: "success",
            });
          }
          failure.forEach((f) => {
            const zap = zaps.find((z) => z.id === f.id);
            createNotification({
              message: t("errors.unable-to-change-owner", {
                hasTitle: zap?.title ? "with-title" : "",
                title: zap?.title || "",
              }),
              type: "error",
            });
          });
        },
        onError: (error) => {
          createNotification({
            title: t("errors.unable-to-change-owner", {
              hasTitle: zaps[0]?.title ? "with-title" : "",
              title: zaps[0]?.title || "",
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          onSettled?.();
        },
      },
    );
    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return <ZapChangeOwnerModalContent {...props} onSubmit={submit} />;
}
